// ════════════════════════════════════════════════════════════════════════
//  Fortschritt
//
//  Reiner Gerätespeicher – im Gastmodus ist das der ganze Fortschritt, im
//  Klassenbetrieb (Phase 5) die Quelle für den Abgleich mit dem Server.
//
//  Zusammenführen mit greatest(): nur Verbesserungen überschreiben. Genau wie
//  student_save_progress im SQL. Läuft das auseinander, zeigen App und
//  Lehrer-Dashboard unterschiedliche Zahlen für dasselbe Kind.
//
//  Was hier NICHT liegt: der Übungscode. Der gehört in den sessionStorage,
//  damit er mit dem Tab stirbt – auf einem Koffer-iPad wechseln die Kinder.
// ════════════════════════════════════════════════════════════════════════

import type { StageResult } from "@otto/lehrgang";

const STORAGE_KEY = "otto.progress.v1";

export type StageProgress = {
  readonly accuracy: number;
  readonly stars: 1 | 2 | 3;
  /** ISO-Zeitstempel des letzten Fortschritts. */
  readonly updated: string;
};

/** stageKey → Fortschritt. */
export type Progress = Readonly<Record<string, StageProgress>>;

export const EMPTY: Progress = Object.freeze({});

function isStageProgress(value: unknown): value is StageProgress {
  if (typeof value !== "object" || value === null) return false;
  const w = value as Record<string, unknown>;
  return (
    typeof w["accuracy"] === "number" &&
    w["accuracy"] >= 0 &&
    w["accuracy"] <= 1 &&
    (w["stars"] === 1 || w["stars"] === 2 || w["stars"] === 3) &&
    typeof w["updated"] === "string"
  );
}

/**
 * Liest den Fortschritt und wirft alles weg, was nicht passt.
 *
 * Ein halb kaputter Eintrag darf die App nicht anhalten: das Kind sitzt davor.
 * Lieber eine Stufe ohne Sterne als ein weißer Bildschirm.
 */
export function loadProgress(): Progress {
  let raw: string | null = null;
  try {
    raw = globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    return EMPTY;
  }
  if (raw === null) return EMPTY;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return EMPTY;
    const clean: Record<string, StageProgress> = {};
    for (const [stageKey, value] of Object.entries(parsed)) {
      if (isStageProgress(value)) clean[stageKey] = value;
    }
    return clean;
  } catch {
    return EMPTY;
  }
}

function write(progress: Progress): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch {
    // Privater Modus oder blockierte Website-Daten: dann eben nur diese Sitzung.
  }
}

/**
 * Ersetzt den Gerätestand vollständig.
 *
 * Beim Code-Login ist der Serverstand maßgeblich: Ein geteiltes iPad darf den
 * Fortschritt des vorherigen Codes nicht in den nächsten hineinmischen.
 */
export function replaceProgress(progress: Progress): Progress {
  write(progress);
  return progress;
}

/**
 * Führt ein Stufenergebnis in den Fortschritt ein und gibt den neuen Stand zurück.
 * Verschlechterungen werden verworfen – ein schlechter Tag löscht keine Sterne.
 */
export function merge(
  progress: Progress,
  result: StageResult,
  now: () => Date = () => new Date(),
): Progress {
  const before = progress[result.stageKey];
  if (before && before.accuracy >= result.accuracy && before.stars >= result.stars) {
    return progress;
  }
  return {
    ...progress,
    [result.stageKey]: {
      accuracy: Math.max(before?.accuracy ?? 0, result.accuracy),
      stars: Math.max(before?.stars ?? 0, result.stars) as 1 | 2 | 3,
      updated: now().toISOString(),
    },
  };
}

/** Speichert ein Stufenergebnis und gibt den neuen Stand zurück. */
export function saveStage(result: StageResult): Progress {
  const neu = merge(loadProgress(), result);
  write(neu);
  return neu;
}

/**
 * Löscht den Fortschritt dieses Geräts.
 *
 * Nötig, weil sich auf einem Koffer-iPad Kinder abwechseln: ohne diesen Weg
 * würde das nächste Kind die Sterne des vorherigen sehen.
 */
export function clearProgress(): Progress {
  try {
    globalThis.localStorage?.removeItem(STORAGE_KEY);
  } catch {
    // siehe schreibe
  }
  return EMPTY;
}
